<template>
    <AllowPermission :permission="permission">
        <div class="report-template-container">
            <UiState v-if="loading" type="loading" title="Loading report" />
            <div v-else class="report-content">
                <div class="report-tabs">
                    <TabContainer @change="handleChangeTab" :tabs="Object.values(REPORT_TABS)" :selectedTab="selectedTab" />
                </div>

                <div v-if="selectedTab.name == REPORT_TABS.REPORT.name" class="report-main-content">
                    <div class="report-header">
                        <SectionTitle>{{ title }}</SectionTitle>
                    </div>

                    <div class="report-filters-section">
                        <div class="filters-header">
                            <h3>Report Configuration</h3>
                            <div class="reports-actions reports-actions--inline">
                                <Button type="button" @click="exportData" variant="secondary" :disabled="exporting">
                                    {{ exporting ? "Exporting..." : "Export" }}
                                    <i class="fas fa-download"></i>
                                </Button>

                                <Button type="button" @click="fetchData(false, false, true)" variant="primary" :disabled="fetchingReport">
                                    {{ fetchingReport ? "Running..." : "Run Report" }}
                                    <i class="fas fa-play"></i>
                                </Button>
                            </div>
                        </div>
                        <div class="filters-grid filters-grid--compact">
                            <div class="filter-field" v-if="showDateFilter && hasDateRanges">
                                <label class="filter-label">Date Range</label>
                                <div class="filter-control">
                                    <SelectDropdown
                                        :options="dateRanges"
                                        :multiple="false"
                                        :value="dateRange"
                                        @change="dateRange = $event"
                                    />
                                    <div v-if="dateRange == -1" class="date-range-inline">
                                        <input
                                            :max="todayBusinessDate"
                                            v-model="customStartDate"
                                            class="form-control form-control-sm"
                                            type="date"
                                        />
                                        <span class="dash">–</span>
                                        <input
                                            :min="customStartDate || ''"
                                            :max="todayBusinessDate"
                                            v-model="customEndDate"
                                            class="form-control form-control-sm"
                                            type="date"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="filter-field" v-for="filter of filters" :key="filter.queryKey">
                                <label class="filter-label">{{ filter.title }}</label>
                                <div class="filter-control">
                                    <SelectDropdown
                                        :options="filter.options"
                                        :multiple="filter.multiple"
                                        :value="filtersSelectedData[filter.queryKey]"
                                        :placeholder="getPlaceholderForFilter(filter)"
                                        @change="updateFilterSelectedData(filter.queryKey, $event)"
                                    />
                                </div>
                            </div>
                        </div>


                    </div>

                    <slot />

                    <div class="report-results">
                        <ReportResultsTable
                            v-if="reportData.length"
                            :report-data="reportData"
                            :columns="columns"
                            :page="page"
                            :total-pages="totalPages"
                            @page-change="updatePage"
                        />

                        <UiState
                            v-else-if="reportError"
                            type="error"
                            :message="reportError"
                            actionLabel="Run again"
                            @action="fetchData(false, false, true)"
                        />
                        <UiState
                            v-else-if="searched && !reportData.length"
                            type="empty"
                            title="No report data found"
                            message="Try adjusting your filters or date range to see results."
                        />
                    </div>
                </div>

                <ReportEmailSubscriptions
                    v-else
                    :create-mode="createEmailScheduleMode"
                    :report-type-code="reportTypeCode"
                    :email-schedules="emailSchedules"
                    :can-manage-schedules="canManageEmailSchedules"
                    @create="canManageEmailSchedules && (createEmailScheduleMode = true)"
                    @created="handleScheduleCreated"
                    @cancel="createEmailScheduleMode = false"
                    @delete="handleScheduleDelete"
                />
            </div>
        </div>
    </AllowPermission>
</template>

<script>
import { mapState } from "vuex"
import { http } from "../../utils/http"
import SelectDropdown from "../crud/SelectDropdown.vue"
import Button from "../UI/Button.vue"
import UiState from "../UI/UiState.vue"
import SectionTitle from "../UI/SectionTitle.vue"
import TabContainer from "../UI/TabContainer.vue"
import TitleBySideItem from "../UI/TitleBySideItem.vue"
import ReportEmailSubscriptions from "./ReportEmailSubscriptions.vue"
import ReportResultsTable from "./ReportResultsTable.vue"
import { confirmDialog } from "../../utils/common"
import AllowPermission from "../layout/AllowPermission.vue"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { businessToday, isBusinessDate } from "../../utils/business-date"

const REPORT_TABS = {
    REPORT: {
        name: "REPORT",
        value: "Report",
    },
    EMAIL: {
        name: "EMAIL",
        value: "Email Subscription",
    },
}

export default {
    emits: ["onResponse"],
    components: {
        ReportEmailSubscriptions,
        ReportResultsTable,
        TabContainer,
        Button,
        UiState,
        SectionTitle,
        TitleBySideItem,
        SelectDropdown,
        AllowPermission,
    },
    props: {
        permission: {
            type: String,
            default: "",
        },
        reportTypeCode: {
            type: String,
            default: "",
        },
        fetchRoute: {
            type: String,
            default: "",
        },
        loading: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: "",
        },
        showDateFilter: {
            type: Boolean,
            default: true,
        },
        filters: {
            type: Array,
            default: () => [],
        },
        columns: {
            type: Array,
            default: [],
            // should be {key: '', title: ''}, where key is used to retrieve value from data and title is used to show data
        },
    },
    data() {
        return {
            exporting: false,
            createEmailScheduleMode: false,
            selectedTab: REPORT_TABS.REPORT,
            REPORT_TABS,
            filtersSelectedData: {},
            reportData: [],
            warehouseId: null,
            dateRange: 2,
            fetchingReport: false,
            searched: false,
            totalPages: 0,
            page: 1,
            limit: 15,
            reportError: "",
            emailSchedules: [],
            customStartDate: "",
            customEndDate: "",
            routeDateScopeActive: false,
            routeWarehouseScopeActive: false,
            reportRequestGeneration: 0,
            suppressNextPageFetch: false,
            businessClockTick: Date.now(),
            businessClockInterval: null,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        ...mapState("misc", ["warehouses", "dateRanges"]),
        hasDateRanges() {
            // guard against undefined dateRanges during initial load
            return Array.isArray(this.dateRanges) && this.dateRanges.length > 0
        },
        canManageEmailSchedules() {
            return checkHasPermission({
                isAdmin: Boolean(this.user?.isAdmin),
                permissionId: PERMISSION_CODES.CMS_MANAGE_REPORT_EMAIL_SCHEDULE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        todayBusinessDate() {
            void this.businessClockTick
            return businessToday()
        },
        includePilotSales() {
            return Boolean(this.user && this.user.isAdmin && this.$route && this.$route.query.includePilotSales === "true")
        },
    },
    mounted() {
        this.initFilters()
        this.syncRouteScope()
        this.fetchEmailSchedules()
        this.businessClockInterval = setInterval(() => {
            this.businessClockTick = Date.now()
        }, 60000)
    },
    beforeDestroy() {
        if (this.businessClockInterval) clearInterval(this.businessClockInterval)
    },
    methods: {
        initializeRouteDateRange() {
            const start = this.$route?.query?.date_range_start
            const end = this.$route?.query?.date_range_end
            if (
                typeof start === "string" &&
                typeof end === "string" &&
                isBusinessDate(start) &&
                isBusinessDate(end) &&
                start <= end
            ) {
                this.dateRange = -1
                this.customStartDate = start
                this.customEndDate = end
                this.routeDateScopeActive = true
            } else if (this.routeDateScopeActive) {
                this.dateRange = 2
                this.customStartDate = ""
                this.customEndDate = ""
                this.routeDateScopeActive = false
            }
        },
        initializeRouteFilters() {
            const warehouses = this.$route?.query?.warehouse_ids
            const warehouseFilter = this.filters.find(filter => filter?.queryKey === "warehouse_ids")
            if (!warehouseFilter) return
            if (typeof warehouses === "string" && warehouses.trim()) {
                const values = warehouses.split(",").map(value => value.trim()).filter(Boolean)
                this.updateFilterSelectedData("warehouse_ids", warehouseFilter.multiple ? values : values[0] || "")
                this.routeWarehouseScopeActive = true
            } else if (this.routeWarehouseScopeActive) {
                this.updateFilterSelectedData("warehouse_ids", warehouseFilter.multiple ? [""] : "")
                this.routeWarehouseScopeActive = false
            }
        },
        syncRouteScope(clearResults = false) {
            this.initializeRouteDateRange()
            this.initializeRouteFilters()
            if (clearResults) {
                this.reportRequestGeneration += 1
                this.fetchingReport = false
                if (this.page !== 1) {
                    this.suppressNextPageFetch = true
                    this.page = 1
                }
                this.searched = false
                this.reportData = []
                this.totalPages = 0
                this.reportError = ""
            }
        },
        getPlaceholderForFilter(filter) {
            // Provide descriptive placeholders based on filter type
            switch (filter.queryKey) {
                case 'warehouse_ids':
                    return 'Choose locations...'
                case 'item_group_ids':
                    return 'Select classifications...'
                case 'show_out_of_stock':
                    return 'Select stock status...'
                case 'sort_by':
                    return 'Product Name A-Z (Default)'
                default:
                    return `Select ${filter.title.toLowerCase()}...`
            }
        },
        handleScheduleCreated() {
            this.createEmailScheduleMode = false
            this.fetchEmailSchedules()
        },
        async handleScheduleDelete(id) {
            if (!this.canManageEmailSchedules) return

            const confirmed = await confirmDialog({
                title: "Delete email schedule?",
                text: "This report email subscription will be removed.",
                confirmButtonText: "Delete",
            })
            if (!confirmed) return

            try {
                this.emailSchedules = this.emailSchedules.filter((item) => item.id != id)
                await http.delete(`/report-email-schedules/${id}`)
                this.$toast.success("Deleted successfully!")
            } catch (err) {
                console.log("error in fetching email schedules", err)
                this.$toast.error("Could not delete!")
            }
        },
        handleChangeTab(e) {
            this.selectedTab = e
        },
        async fetchEmailSchedules() {
            try {
                const res = await http.get(`/report-email-schedules?report_type_code=${this.reportTypeCode}`)
                this.emailSchedules = res.data.result
            } catch (err) {
                console.log("error in fetching email schedules", err)
            }
        },
        initFilters() {
            for (let filter of this.filters) {
                if (!filter || !filter.queryKey) continue
                // only set default if not already set (prevents overriding user selection)
                const isUnset = this.filtersSelectedData[filter.queryKey] === undefined
                if (isUnset && Array.isArray(filter.options) && filter.options.length > 0) {
                    const defaultVal = filter.multiple ? [filter.options[0].id] : filter.options[0].id
                    this.updateFilterSelectedData(filter.queryKey, defaultVal)
                }
            }
        },
        async exportData() {
            this.exporting = true
            try {
                await this.fetchData(true, false)
            } catch (err) {
                console.log("error in exporting data", err)
            }
            this.exporting = false
        },
        updateFilterSelectedData(key, value) {
            this.filtersSelectedData = { ...this.filtersSelectedData, [key]: value }
        },
        updatePage(e) {
            this.page = e
        },
        buildRequestParams(exportMode = false, pdfMode = false) {
            const params = {}

            Object.keys(this.filtersSelectedData)
                .filter((item) => !!item)
                .forEach((itemKey) => {
                    let value = this.filtersSelectedData[itemKey]
                    if (Array.isArray(value)) {
                        value = value.join(",")
                    }
                    params[itemKey] = value || ""
                })

            params.date_type_id = this.dateRange || ""
            params.page = exportMode ? "" : this.page
            params.limit = exportMode ? "999" : this.limit
            params.date_range_start = this.customStartDate || ""
            params.date_range_end = this.customEndDate || ""
            params.export_mode = exportMode ? "true" : "false"
            if (this.includePilotSales) {
                params.includePilotSales = "true"
            }
            params.columns = exportMode
                ? this.columns.map((item) => `${item.key}___${item.title.replaceAll(" ", "_")}`).join(",")
                : ""
            params.pdf_mode = pdfMode ? "true" : "false"

            return params
        },
        openBlobResponse(response, fallbackName) {
            const contentDisposition = response.headers && response.headers["content-disposition"]
            const filenameMatch = contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/)
            const filename = filenameMatch ? filenameMatch[1] : fallbackName
            const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/octet-stream" })
            const blobUrl = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = blobUrl
            link.download = filename
            link.rel = "noopener noreferrer"
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(blobUrl)
        },
        async fetchData(exportMode = false, pdfMode = false, resetPage = false) {
            if (this.fetchingReport) return
            if (resetPage) {
                if (this.page !== 1) {
                    this.page = 1
                    return
                }
            }
            if (this.dateRange == -1 && !this.customStartDate && !this.customEndDate) {
                return this.$toast.error("Please enter start and end date!")
            }

            const requestGeneration = ++this.reportRequestGeneration
            this.fetchingReport = true
            this.reportError = ""

            try {
                const params = this.buildRequestParams(exportMode, pdfMode)

                if (exportMode) {
                    const response = await http.get(this.fetchRoute, {
                        params,
                        responseType: "blob",
                    })
                    if (requestGeneration !== this.reportRequestGeneration) return
                    this.openBlobResponse(response, pdfMode ? "report.pdf" : "report.xlsx")
                } else {
                    const res = await http.get(this.fetchRoute, { params })
                    if (requestGeneration !== this.reportRequestGeneration) return

                    this.$emit("onResponse", res.data)
                    this.totalPages = res.data.totalPages
                    this.searched = true
                    this.reportData = res.data.result
                }
            } catch (err) {
                if (requestGeneration !== this.reportRequestGeneration) return
                console.log("error in fetching data", err)
                this.reportError = "Could not fetch this report."
                this.$toast.error("Could not fetch reports!")
            } finally {
                if (requestGeneration === this.reportRequestGeneration) {
                    this.fetchingReport = false
                }
            }
        },
    },
    watch: {
        page(e) {
            if (this.suppressNextPageFetch) {
                this.suppressNextPageFetch = false
                return
            }
            this.fetchData()
        },
        filters: {
            deep: true,
            handler() {
                this.initFilters()
                this.initializeRouteFilters()
            },
        },
        "$route.query": {
            deep: true,
            handler() {
                this.syncRouteScope(true)
            },
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.report-template-container { padding: $cardPaddingY $cardPaddingX; min-height: 600px; }

.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;

    .loading-content {
        text-align: center;
        color: $primaryColor;

        .loading-icon {
            font-size: 48px;
            margin-bottom: 16px;
            animation: spin 2s linear infinite;
        }

        p {
            font-size: 16px;
            margin: 0;
        }
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.report-content {
    max-width: 100%;
}

.report-tabs { margin-bottom: $sectionGap * 2; }

.report-header { margin-bottom: $sectionGap * 2; padding-bottom: $sectionGap; border-bottom: 2px solid $lightBorderColor; }

.report-filters-section {
    background: $lightCardBg;
    padding: $cardPaddingY $cardPaddingX;
    border-radius: $cardRadius;
    border: 1px solid $lightBorderColor;
    margin-bottom: $sectionGap * 2;
    overflow: visible; // allow dropdowns to overflow outside card

    .filters-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 8px;

        h3 {
            color: $primaryColor;
            font-size: 18px;
            font-weight: 700;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;

            &::before {
                content: '⚙️';
                font-size: 18px;
            }
        }
    }
}

.filters-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: $sectionGap; margin-bottom: $sectionGap; align-items: start; overflow: visible; }

.filters-grid--compact .filter-field { grid-column: span 3; }
.filters-grid--compact .filter-label {
    font-size: 12px;
    font-weight: 600;
    color: $textColor;
    margin-bottom: 6px;
    display: block;
}
.filters-grid--compact .filter-control :deep(.multiselect),
.filters-grid--compact .filter-control :deep(.form-select) {
    min-height: 38px;
}
.date-range-inline { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
.date-range-inline .dash { color: $lightBorderColor; }

.filter-item {
    margin: 0;
    width: 100%;
    grid-column: span 3;

    .select-container {
        width: 100%;
        position: relative;

        .multiselect {
            width: 100%;
            min-width: 220px;
            min-height: 38px;
            border-radius: 8px;
            border: 2px solid $lightBorderColor;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
            transition: border-color 0.2s ease, box-shadow 0.2s ease;

            &:hover {
                border-color: lighten($primaryColor, 20%);
            }

            &.multiselect--active {
                border-color: $primaryColor;
                box-shadow: 0 0 0 3px rgba($primaryColor, 0.15);
            }
        }

        .multiselect__tags {
            width: 100%;
            min-height: 38px;
            padding: 6px 36px 0 10px;
            border-radius: 8px;
            border: 2px solid $lightBorderColor;
            background: $lightCardBg;
            font-size: 14px;
            box-sizing: border-box;
        }

        .multiselect__single {
            font-size: 14px;
            font-weight: 500;
            color: $textColor;
            padding-left: 4px;
            margin-bottom: 10px;
            line-height: 1.4;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: calc(100% - 40px);
        }

        .multiselect__placeholder {
            font-size: 14px;
            color: lighten($textColor, 30%);
            padding-left: 4px;
            margin-bottom: 10px;
            line-height: 1.4;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: calc(100% - 40px);
        }

        .multiselect__select {
            height: 34px;
            width: 34px;

            &:before {
                color: $primaryColor;
                border-color: $primaryColor transparent transparent;
            }
        }

        .form-select {
            width: 100%;
            min-width: 220px;
            min-height: 38px;
            padding: 8px 10px;
            font-size: 14px;
            font-weight: 500;
            border: 2px solid $lightBorderColor;
            border-radius: 8px;
            color: $textColor;
            background-color: $lightCardBg;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
            box-sizing: border-box;

            &:focus {
                border-color: $primaryColor;
                box-shadow: 0 0 0 3px rgba($primaryColor, 0.15);
                outline: none;
            }

            &:hover {
                border-color: lighten($primaryColor, 20%);
            }
        }

        .multiselect__content-wrapper {
            border: 2px solid $lightBorderColor;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
            margin-top: 2px;
            z-index: 1000;
        }

        .multiselect__option {
            font-size: 14px;
            padding: 12px 16px;
            line-height: 1.4;
            color: $textColor;
            font-weight: 500;
            border-bottom: 1px solid lighten($lightBorderColor, 5%);

            &:hover {
                background-color: lighten($primaryColor, 45%);
                color: $primaryColor;
            }

            &.multiselect__option--highlight {
                background-color: $primaryColor;
                color: white;
            }

            &.multiselect__option--selected {
                background-color: lighten($primaryColor, 40%);
                color: $primaryColor;
                font-weight: 600;
            }

            &:last-child {
                border-bottom: none;
            }
        }
    }
}

.date-range-inputs {
    display: flex;
    gap: 16px;
    margin-top: 12px;
}

.date-input-group {
    flex: 1;

    label {
        display: block;
        font-weight: 500;
        color: $textColor;
        margin-bottom: 6px;
        font-size: 14px;
    }
}

.reports-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid $lightBorderColor;

    .btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 14px;
        transition: all 0.2s ease;
        border: none;
        cursor: pointer;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none !important;
        }

        &.btn-primary {
            background: linear-gradient(135deg, $primaryColor 0%, darken($primaryColor, 5%) 100%);
            color: white;

            &:hover:not(:disabled) {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(13, 45, 44, 0.3);
            }
        }

        &.btn-secondary {
            background: linear-gradient(135deg, $secondaryColor 0%, darken($secondaryColor, 5%) 100%);
            color: white;

            &:hover:not(:disabled) {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(186, 142, 88, 0.3);
            }
        }
    }
}

.reports-actions--inline {
    padding-top: 0;
    border-top: none;
}

.report-results {
    margin-top: 32px;
}

.no-data-message {
    text-align: center;
    padding: 60px 40px;
    color: rgba($textColor, 0.6);

    .no-data-icon {
        font-size: 64px;
        color: $lightBorderColor;
        margin-bottom: 20px;
    }

    h4 {
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: $textColor;
    }

    p {
        font-size: 16px;
        margin: 0;
    }
}

.empty-state {
    text-align: center;
    padding: 40px 20px;
    color: rgba($textColor, 0.6);

    i {
        font-size: 48px;
        color: $lightBorderColor;
        margin-bottom: 16px;
    }

    p {
        font-size: 16px;
        margin: 0;
    }
}

/* Form Control Improvements */
:deep(.form-control) {
    border-radius: 10px;
    border: 1px solid $lightBorderColor;
    padding: 12px 16px;
    font-size: 14px;
    transition: all 0.2s ease;

    &:focus {
        border-color: $primaryColor;
        box-shadow: 0 0 0 3px rgba(13, 45, 44, 0.1);
        outline: none;
    }
}

:deep(.section-title) {
    color: $primaryColor;
    font-size: 28px;
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.02em;
}

:deep(.title-by-side-item) {
    margin-bottom: 0;

    .title {
        color: $textColor;
        font-weight: 600;
        margin-bottom: 8px;
        font-size: 14px;
    }
}

:deep(.custom-table) {
    border-radius: 0;
    box-shadow: none;
    border: none;

    th {
        background: linear-gradient(135deg, $primaryColor 0%, darken($primaryColor, 5%) 100%);
        color: white;
        font-weight: 600;
        padding: 16px 20px;
        border: none;
        font-size: 14px;
    }

    td {
        padding: 16px 20px;
        border-bottom: 1px solid $lightBorderColor;
        vertical-align: middle;

        &:last-child {
            border-right: none;
        }
    }

    tr:hover {
        background-color: $lightHoverColor;
    }

    tr:last-child td {
        border-bottom: none;
    }
}

/* Tab styling improvements */
:deep(.tab-container) {
    .tab-item {
        border-radius: 10px 10px 0 0;
        padding: 14px 28px;
        font-weight: 600;
        font-size: 14px;
        border: 1px solid $lightBorderColor;
        border-bottom: none;
        background: $lightCardBg;
        color: $textColor;
        transition: all 0.2s ease;

        &:hover {
            background: $lightHoverColor;
        }

        &.active {
            background: $primaryColor;
            color: white;
            border-color: $primaryColor;
        }
    }
}

/* Responsive improvements */
@media (max-width: 1280px) {
    .filter-item { grid-column: span 4; }
    .filters-grid--compact .filter-field { grid-column: span 4; }
}

@media (max-width: 1024px) {
    .filter-item { grid-column: span 6; }
    .filters-grid--compact .filter-field { grid-column: span 6; }
}

@media (max-width: 768px) {
    .report-template-container {
        padding: 20px;
    }

    .report-filters-section {
        padding: 14px;
    }

    .filters-grid { grid-template-columns: repeat(6, 1fr); gap: 10px; }
    .filter-item { grid-column: span 6; }
    .filters-grid--compact .filter-field { grid-column: span 6; }

    .date-range-inputs {
        flex-direction: column;
        gap: 12px;
    }

    .reports-actions {
        flex-direction: column;
        gap: 12px;

        .btn {
            width: 100%;
            justify-content: center;
        }
    }

    .results-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;

        .results-count {
            font-size: 13px;
        }
    }

    .email-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    :deep(.custom-table) {
        font-size: 12px;

        th, td {
            padding: 12px 16px;
        }
    }
}

/* Dark theme support via app-level .dark */
:deep(.dark) .report-template-container { background: $backgroundColorDark; }
:deep(.dark) .report-template-container .report-filters-section {
    background: $darkCardBg !important;
    border-color: $darkBorderColor !important;
}
:deep(.dark) .report-template-container .report-filters-section h3 { color: $darkTextColor !important; }
/* Ensure main report title and email section headers are readable in dark mode */
:deep(.dark) .report-template-container .report-header .section-title { color: $darkTextColor !important; }
:deep(.dark) .report-template-container .email-header h3 { color: $darkTextColor !important; }
:deep(.dark) .report-template-container .results-container,
:deep(.dark) .report-template-container .email-schedules { background: $darkCardBg; border-color: $darkBorderColor; }
:deep(.dark) .report-template-container .results-header,
:deep(.dark) .report-template-container .pagination-container { background: darken($darkCardBg, 3%); border-color: $darkBorderColor; }
:deep(.dark) .report-template-container .report-header { border-bottom-color: $darkBorderColor; }
:deep(.dark) .report-template-container .date-range-inputs .form-control {
    background: $darkInputBg;
    border-color: $darkBorderColor;
    color: $darkTextColor;
}
:deep(.dark) .report-template-container .results-header h4 { color: $darkTextColor; }
:deep(.dark) .report-template-container .results-count { color: rgba($darkTextColor, 0.7); }
:deep(.dark) .report-template-container .no-data-message,
:deep(.dark) .report-template-container .empty-state { color: rgba($darkTextColor, 0.7); }
:deep(.dark) .report-template-container .no-data-icon,
:deep(.dark) .report-template-container .empty-state i { color: lighten($darkCardBg, 10%); }

/* Selects/multiselect dark variants */
:deep(.dark) .report-template-container .select-container .multiselect__tags { background: $darkInputBg; border-color: $darkBorderColor; color: $darkTextColor; }
:deep(.dark) .report-template-container .select-container .multiselect__content-wrapper { border-color: $darkBorderColor; }
:deep(.dark) .report-template-container .form-select { background: $darkInputBg; border-color: $darkBorderColor; color: $darkTextColor; }

</style>
